/**
 * DevicePanel - Device info and debug console
 * Connection is handled by ConnectionBar in the header
 * Configuration upload is handled by ExportImportPanel
 */

import { useState, useEffect, useRef } from 'react';
import {
  SerialConnection,
  DeviceInfo,
  getSerialConnection,
} from '@/lib/serialConnection';
import { useConfigStore } from '@/store/configStore';

export function DevicePanel() {
  const [supported, setSupported] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);
  const [consoleOutput, setConsoleOutput] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [customCommand, setCustomCommand] = useState('');

  const consoleRef = useRef<HTMLDivElement>(null);
  const connectionRef = useRef<SerialConnection | null>(null);

  const liveVizEnabled = useConfigStore((s) => s.liveVisualizationEnabled);
  const setLiveVizEnabled = useConfigStore((s) => s.setLiveVisualizationEnabled);

  // Setup connection callbacks and check status (connection managed by ConnectionBar)
  useEffect(() => {
    setSupported(SerialConnection.isSupported());

    const conn = getSerialConnection();
    connectionRef.current = conn;

    // Subscribe to data for console output
    const unsubData = conn.onData((data) => {
      setConsoleOutput((prev) => {
        const newOutput = [...prev];
        const lastLine = newOutput[newOutput.length - 1];
        if (newOutput.length > 0 && lastLine && !lastLine.endsWith('\n')) {
          newOutput[newOutput.length - 1] = lastLine + data;
        } else {
          newOutput.push(data);
        }
        while (newOutput.length > 100) {
          newOutput.shift();
        }
        return newOutput;
      });
    });

    // Subscribe to state changes
    const unsubState = conn.onStateChange((state) => {
      setIsConnected(state === 'connected');
      if (state === 'error') {
        setError('Connection lost');
      }
      if (state === 'connected') {
        // Fetch device info when connected
        conn.getDeviceInfo().then(setDeviceInfo).catch(() => {});
      }
      if (state === 'disconnected') {
        setDeviceInfo(null);
      }
    });

    return () => {
      unsubData();
      unsubState();
    };
  }, []);

  // Auto-scroll console
  useEffect(() => {
    if (consoleRef.current) {
      consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
    }
  }, [consoleOutput]);

  const handleRefreshInfo = async () => {
    const conn = connectionRef.current;
    if (!conn?.isConnected()) return;

    try {
      const info = await conn.getDeviceInfo();
      setDeviceInfo(info);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get device info');
    }
  };

  const handleSendCommand = async () => {
    const conn = connectionRef.current;
    if (!conn?.isConnected() || !customCommand.trim()) return;

    setConsoleOutput((prev) => [...prev, `> ${customCommand}\n`]);
    try {
      await conn.sendCommand(customCommand);
    } catch (err) {
      console.error('Command failed:', err);
    }
    setCustomCommand('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSendCommand();
    }
  };

  if (!supported) {
    return (
      <div style={{ padding: '12px', color: '#dc3545' }}>
        <strong>Web Serial API not supported</strong>
        <p style={{ fontSize: '12px', marginTop: '8px' }}>
          Please use Chrome, Edge, or Opera browser.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Not connected message */}
      {!isConnected && (
        <div style={{
          padding: '12px',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeeba',
          borderRadius: '4px',
          color: '#856404',
          fontSize: '12px'
        }}>
          Use the <strong>Connect Device</strong> button in the connection bar above.
        </div>
      )}

      {/* Error display */}
      {error && (
        <div
          style={{
            padding: '8px',
            backgroundColor: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            color: '#721c24',
            fontSize: '12px',
          }}
        >
          {error}
        </div>
      )}

      {/* Device info */}
      {isConnected && deviceInfo && (
        <div
          style={{
            padding: '10px',
            backgroundColor: '#e8f5e9',
            borderRadius: '4px',
            fontSize: '12px',
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>
            Device Info
            <button
              onClick={handleRefreshInfo}
              style={{
                marginLeft: '8px',
                padding: '2px 6px',
                fontSize: '10px',
                border: '1px solid #ccc',
                borderRadius: '3px',
                cursor: 'pointer',
                backgroundColor: '#fff',
              }}
            >
              Refresh
            </button>
          </div>
          <div>Firmware: {deviceInfo.firmware}</div>
          <div>Build: {deviceInfo.build}</div>
          <div>MCU: {deviceInfo.mcu}</div>
          <div>Uptime: {Math.round(deviceInfo.uptime / 1000)}s</div>
          <div>
            Modules: {deviceInfo.modulesOnline}/{deviceInfo.modulesRegistered} online
          </div>
          <div>
            Config: {deviceInfo.configLoaded ? 'Loaded' : 'Not loaded'}
            {deviceInfo.configLoaded && deviceInfo.gridCols && (
              <span>
                {' '}
                ({deviceInfo.gridCols}x{deviceInfo.gridRows}, {deviceInfo.totalKeys} keys)
              </span>
            )}
          </div>
        </div>
      )}

      {/* Live visualization toggle */}
      {isConnected && (
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={liveVizEnabled}
            onChange={(e) => setLiveVizEnabled(e.target.checked)}
          />
          Live key visualization
        </label>
      )}

      {/* Console output */}
      {isConnected && (
        <div>
          <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>Debug Console</div>
          <div
            ref={consoleRef}
            style={{
              height: '200px',
              overflow: 'auto',
              backgroundColor: '#1e1e1e',
              color: '#d4d4d4',
              fontFamily: 'monospace',
              fontSize: '11px',
              padding: '8px',
              borderRadius: '4px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {consoleOutput.join('')}
          </div>

          {/* Command input */}
          <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
            <input
              type="text"
              value={customCommand}
              onChange={(e) => setCustomCommand(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter command..."
              style={{
                flex: 1,
                padding: '6px 8px',
                fontSize: '12px',
                fontFamily: 'monospace',
                border: '1px solid #ced4da',
                borderRadius: '4px',
              }}
            />
            <button
              onClick={handleSendCommand}
              disabled={!customCommand.trim()}
              style={buttonStyle}
            >
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const buttonStyle: React.CSSProperties = {
  padding: '6px 12px',
  fontSize: '12px',
  fontWeight: 500,
  color: '#fff',
  backgroundColor: '#007bff',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
};
